import { useParams, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Network,
  ExternalLink,
  Download,
  Info,
  Dna,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Skeleton } from '../components/ui/Skeleton'
import { MoleculeViewer3DWithErrorBoundary as MoleculeViewer3D } from '../components/MoleculeViewer3D'
import { useComplexDetail } from '../api/hooks'
import { getNcbiProteinUrl } from '../utils/externalLinks'

/**
 * Complex Detail Page
 * Shows protein-protein interaction details:
 * - ipSAE, ipSAE_min, ipTM scores
 * - 3D structure viewer for complex model (when available)
 * - Links to both protein pages
 */
export function ComplexDetailPage() {
  const { clusterId, protein1Id, protein2Id } = useParams<{
    clusterId: string
    protein1Id: string
    protein2Id: string
  }>()

  const { data: complexData, isLoading, error } = useComplexDetail(
    clusterId || '',
    protein1Id || '',
    protein2Id || ''
  )

  const [structureContent, setStructureContent] = useState<string | null>(null)
  const [structureLoading, setStructureLoading] = useState(false)

  const isCifFormat = complexData?.structure_url?.endsWith('.cif') ?? false
  const structureFormat = isCifFormat ? 'cif' : 'pdb'

  // Load complex structure file if available
  useEffect(() => {
    if (complexData?.structure_url) {
      setStructureLoading(true)
      fetch(complexData.structure_url)
        .then((response) => {
          if (!response.ok) throw new Error('Failed to fetch structure')
          return response.text()
        })
        .then((text) => {
          setStructureContent(text)
        })
        .catch(() => {
          setStructureContent(null)
        })
        .finally(() => {
          setStructureLoading(false)
        })
    }
  }, [complexData?.structure_url])

  // Score badge variant helper
  const getScoreBadgeVariant = (score: number, threshold: number = 0.7): 'green' | 'amber' | 'default' => {
    if (score >= threshold) return 'green'
    if (score >= 0.6) return 'amber'
    return 'default'
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <p className="text-red-400">Failed to load complex data</p>
        <Link to={`/clusters/${clusterId}`}>
          <Button variant="secondary" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            Back to Cluster
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/clusters/${clusterId}`}>
          <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            Back to Cluster
          </Button>
        </Link>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Network className="w-8 h-8 text-accent-green" />
          Protein Complex Prediction
        </h1>
        <p className="text-gray-400 mt-2">
          Predicted interaction between{' '}
          <span className="font-mono text-accent-cyan">{protein1Id}</span>
          {' '}and{' '}
          <span className="font-mono text-accent-cyan">{protein2Id}</span>
        </p>
      </motion.div>

      {isLoading ? (
        <div className="space-y-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : complexData ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Scores Card */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-accent-purple" />
                Prediction Scores
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* ipTM Score */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">ipTM</span>
                  <Badge variant={getScoreBadgeVariant(complexData.iptm)}>
                    {complexData.iptm.toFixed(3)}
                  </Badge>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${complexData.iptm >= 0.7 ? 'bg-accent-green' : complexData.iptm >= 0.6 ? 'bg-accent-amber' : 'bg-gray-500'}`}
                    style={{ width: `${complexData.iptm * 100}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Interface predicted TM-score (higher = more confident)
                </p>
              </div>

              {/* ipSAE Score */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">ipSAE</span>
                  <Badge variant={getScoreBadgeVariant(complexData.ipsae, 0.5)}>
                    {complexData.ipsae.toFixed(3)}
                  </Badge>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${complexData.ipsae >= 0.5 ? 'bg-accent-green' : complexData.ipsae >= 0.3 ? 'bg-accent-amber' : 'bg-gray-500'}`}
                    style={{ width: `${Math.min(complexData.ipsae * 100, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Interface pairwise Aligned Error score
                </p>
              </div>

              {/* ipSAE_min Score */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">ipSAE_min</span>
                  <Badge variant={getScoreBadgeVariant(complexData.ipsae_min, 0.5)}>
                    {complexData.ipsae_min.toFixed(3)}
                  </Badge>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${complexData.ipsae_min >= 0.5 ? 'bg-accent-green' : complexData.ipsae_min >= 0.3 ? 'bg-accent-amber' : 'bg-gray-500'}`}
                    style={{ width: `${Math.min(complexData.ipsae_min * 100, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Minimum interface pairwise Aligned Error
                </p>
              </div>

              {/* Interaction Type */}
              <div className="pt-4 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Interaction Type</span>
                  <Badge variant={complexData.is_homo ? 'purple' : 'cyan'}>
                    {complexData.is_homo ? 'Homodimer' : 'Heterodimer'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Proteins Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Dna className="w-5 h-5 text-accent-cyan" />
                Interacting Proteins
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Protein 1 */}
                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <h3 className="text-sm text-gray-400 mb-2">Protein 1</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <a
                        href={getNcbiProteinUrl(protein1Id || '')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-accent-cyan hover:underline flex items-center gap-1"
                      >
                        {protein1Id}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    {complexData.protein1_product && (
                      <p className="text-sm text-gray-300">{complexData.protein1_product}</p>
                    )}
                    <Link to={`/proteins/${clusterId}/${protein1Id}`}>
                      <Button variant="secondary" size="sm" className="mt-2">
                        View Protein Details
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Protein 2 */}
                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <h3 className="text-sm text-gray-400 mb-2">Protein 2</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <a
                        href={getNcbiProteinUrl(protein2Id || '')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-accent-cyan hover:underline flex items-center gap-1"
                      >
                        {protein2Id}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    {complexData.protein2_product && (
                      <p className="text-sm text-gray-300">{complexData.protein2_product}</p>
                    )}
                    <Link to={`/proteins/${clusterId}/${protein2Id}`}>
                      <Button variant="secondary" size="sm" className="mt-2">
                        View Protein Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3D Structure Viewer */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Network className="w-5 h-5 text-accent-green" />
                  Complex Structure
                </span>
                {complexData.structure_url && structureContent && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Download className="w-4 h-4" />}
                    onClick={() => {
                      const ext = isCifFormat ? 'cif' : 'pdb'
                      const blob = new Blob([structureContent], { type: 'text/plain' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `${clusterId}_${protein1Id}_${protein2Id}_complex.${ext}`
                      a.click()
                      URL.revokeObjectURL(url)
                    }}
                  >
                    Download {isCifFormat ? 'CIF' : 'PDB'}
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {structureLoading ? (
                <Skeleton className="h-[500px] w-full" />
              ) : structureContent ? (
                <div className="space-y-4">
                  <MoleculeViewer3D
                    pdbData={structureContent}
                    format={structureFormat}
                    colorScheme="chain"
                    height="500px"
                    domains={[]}
                    sites={[]}
                    paeDomains={[]}
                  />
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#90A0CF' }} />
                      <span>Chain A ({complexData.protein1_id})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#80CF98' }} />
                      <span>Chain B ({complexData.protein2_id})</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 bg-white/5 rounded-lg">
                  <Network className="w-16 h-16 text-gray-600 mb-4" />
                  <p className="text-gray-400 text-center">
                    Complex structure coordinates are not yet available.
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    The predicted complex model will be displayed here when available.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center">
              <Network className="w-16 h-16 text-gray-600 mb-4" />
              <p className="text-gray-400">No complex data found for this protein pair.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default ComplexDetailPage
